if (!customElements.get('variant-card')) {
  customElements.define(
    'variant-card',
    class VariantCard extends HTMLElement {
      connectedCallback() {
        this.card = this.closest('.card-wrapper');
        if (!this.card) return;

        this.swatches = Array.from(this.querySelectorAll('.card-color-swatch'));
        if (!this.swatches.length) return;

        // Ease-in-out crossfade for variant image swaps (skipped for reduced motion).
        this.fadeDuration = 200;
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.fadeAnimation = null;

        // The two stacked <img> in the card media: [0] primary, [1] hover.
        this.mediaImages = this.card.querySelectorAll('.card__media .media img');
        // Product links that should follow the selected variant.
        this.links = this.card.querySelectorAll('a.card-variant-link');

        this.selectedSwatch =
          this.swatches.find((swatch) => swatch.classList.contains('card-color-swatch--active')) ||
          this.swatches[0];

        this.swatches.forEach((swatch) => {
          // Hover previews a variant's image; click makes it the selected one.
          swatch.addEventListener('mouseenter', () => this.applyImages(swatch));
          swatch.addEventListener('click', (event) => this.selectSwatch(event, swatch));
        });
        // Leaving the whole swatch row reverts to the selected variant.
        this.addEventListener('mouseleave', () => this.applyImages(this.selectedSwatch));

        // Single-row carousel: keep the swatches on one line and reveal the
        // prev/next arrows only when they overflow the card (main image) width.
        // Skipped in the upsell layout (.card--horizontal), which stacks the
        // thumbnails in a fixed vertical column instead.
        this.isCarousel = !this.closest('.card--horizontal');
        this.viewport = this.querySelector('.card-color-selector__viewport');
        this.track = this.querySelector('.card-color-selector__track');
        this.prevArrow = this.querySelector('.card-color-selector__arrow--prev');
        this.nextArrow = this.querySelector('.card-color-selector__arrow--next');

        if (this.isCarousel && this.viewport && this.track) {
          this.updateCarousel = this.updateCarousel.bind(this);

          this.prevArrow?.addEventListener('click', (event) => this.scrollTrack(event, -1));
          this.nextArrow?.addEventListener('click', (event) => this.scrollTrack(event, 1));
          this.viewport.addEventListener('scroll', this.updateCarousel, { passive: true });

          if ('ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(this.updateCarousel);
            this.resizeObserver.observe(this);
          } else {
            window.addEventListener('resize', this.updateCarousel);
          }

          // Measure after layout so swatch widths are known.
          requestAnimationFrame(this.updateCarousel);
        }

        // Gate the media hover-swap to the image region only (see onPointerMove).
        this.media = this.card.querySelector('.card__media');
        if (this.media) {
          this.onPointerMove = this.onPointerMove.bind(this);
          this.onPointerLeave = this.onPointerLeave.bind(this);
          this.card.addEventListener('mousemove', this.onPointerMove);
          this.card.addEventListener('mouseleave', this.onPointerLeave);
        }
      }

      applyImages(swatch) {
        if (!swatch) return;
        const { src, srcHover } = swatch.dataset;
        const primary = this.mediaImages[0];
        const secondary = this.mediaImages[1];
        if (!primary || !src) return;

        // Already showing this variant — nothing to do.
        if (primary.getAttribute('src') === src) return;

        const setSrc = (img, value) => {
          if (!img || !value) return;
          img.srcset = '';
          img.src = value;
        };

        // No crossfade layer or motion disabled: swap instantly.
        if (this.reduceMotion || !secondary || typeof secondary.animate !== 'function') {
          setSrc(primary, src);
          setSrc(secondary, srcHover || src);
          return;
        }

        // Cancel any fade still in flight so rapid hovering doesn't stack.
        if (this.fadeAnimation) this.fadeAnimation.cancel();

        // Preload so the fade shows the new image, not a blank frame, then dissolve
        // the new image in over the current one via the (hidden) hover layer.
        const preload = new Image();
        const runFade = () => {
          setSrc(secondary, src);
          const animation = secondary.animate(
            [{ opacity: 0 }, { opacity: 1 }],
            { duration: this.fadeDuration, easing: 'ease-in-out', fill: 'forwards' }
          );
          this.fadeAnimation = animation;
          animation.onfinish = () => {
            // Commit the new image to the primary layer (hidden behind the opaque
            // hover layer), then release the fade and restore the hover image.
            setSrc(primary, src);
            requestAnimationFrame(() => {
              if (this.fadeAnimation === animation) {
                animation.cancel();
                this.fadeAnimation = null;
              }
              setSrc(secondary, srcHover || src);
            });
          };
        };
        preload.onload = runFade;
        preload.onerror = () => {
          setSrc(primary, src);
          setSrc(secondary, srcHover || src);
        };
        preload.src = src;
      }

      selectSwatch(event, swatch) {
        // The card has a stretched title link overlaying everything; block it.
        event.preventDefault();
        event.stopPropagation();

        this.selectedSwatch = swatch;
        this.applyImages(swatch);

        if (swatch.dataset.url) {
          this.links.forEach((link) => {
            link.href = swatch.dataset.url;
          });
        }

        this.swatches.forEach((other) => {
          other.classList.remove('card-color-swatch--active');
          other.removeAttribute('aria-current');
        });
        swatch.classList.add('card-color-swatch--active');
        swatch.setAttribute('aria-current', 'true');
      }

      // Advance the carousel by roughly one viewport-width in `direction` (±1).
      scrollTrack(event, direction) {
        // The card title link is stretched over the whole card; don't let the
        // arrow click bubble up to it.
        event.preventDefault();
        event.stopPropagation();
        if (!this.viewport) return;

        const amount = Math.max(this.viewport.clientWidth * 0.8, 60);
        this.viewport.scrollBy({
          left: amount * direction,
          behavior: this.reduceMotion ? 'auto' : 'smooth',
        });
      }

      // Show each overlaid arrow only when the track can scroll that way, so the
      // first/last swatch stays uncovered at the extremes.
      updateCarousel() {
        if (!this.viewport || !this.track) return;

        // Compare the track's full width to the selector width (≈ the main image
        // width): the arrows are overlaid, so this doesn't depend on their state.
        const scrollable = this.track.scrollWidth - this.clientWidth > 1;
        this.classList.toggle('is-scrollable', scrollable);

        const maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
        const left = this.viewport.scrollLeft;
        if (this.prevArrow) this.prevArrow.hidden = !scrollable || left <= 1;
        if (this.nextArrow) this.nextArrow.hidden = !scrollable || left >= maxScroll - 1;
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        if (this.updateCarousel) window.removeEventListener('resize', this.updateCarousel);
      }

      onPointerMove(event) {
        const rect = this.media.getBoundingClientRect();
        const overMedia =
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom;
        this.card.classList.toggle('card--media-hovered', overMedia);
      }

      onPointerLeave() {
        this.card.classList.remove('card--media-hovered');
      }
    }
  );
}
